/**
 * MobileBookAppointmentBar — persistent bottom-of-viewport CTA on public
 * pages. Mobile-only (hidden ≥ md) so it never competes with the desktop
 * "Book Appointment" nav button.
 *
 * Visibility rules:
 *   • Hidden on admin / dashboard routes (the shell already hides the
 *     public Navbar there, but we short-circuit here for symmetry).
 *   • Hidden on the /booking flow itself and its success/cancel pages —
 *     users are already inside the funnel.
 *   • Hidden on /login and /reset-password so it doesn't overlap the
 *     auth submit button.
 *
 * Design brief: "Luxury SaaS" — clean lines, high contrast, no clutter.
 * Uses the site's own #2D4A3E green + Fraunces-neutral system font stack,
 * a slim glassmorphic backdrop over content behind, safe-area padding
 * for iOS home-indicator, and one tap target that meets Apple's 44×44
 * minimum (this one is 52px tall for comfort).
 */
import { Link, useLocation } from "react-router-dom";
import { Calendar, ArrowRight } from "lucide-react";
import { usePublicPageReady } from "@/components/PublicPageReadiness";

const HIDDEN_ROUTES = [
  "/booking",
  "/login",
  "/register",
  "/reset-password",
  "/admin-setup",
];

export default function MobileBookAppointmentBar() {
  const { pathname } = useLocation();
  const pageReady = usePublicPageReady();

  // Route-level suppression — do this via prefix so /booking/success is
  // also inside the funnel.
  const isSuppressed =
    pathname.startsWith("/admin") ||
    pathname === "/dashboard" ||
    HIDDEN_ROUTES.some((p) => pathname === p || pathname.startsWith(p + "/"));

  // Layout reserves its bottom spacing independently, so showing this fixed
  // bar never pushes page content when initial state has settled.
  if (isSuppressed || !pageReady) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 md:hidden pointer-events-none"
      data-testid="mobile-book-appointment-bar-wrapper"
      aria-hidden={false}
    >
      {/* Fade so long-copy pages don't butt up against the CTA. */}
      <div className="h-6 bg-gradient-to-t from-[#F5F2ED]/85 to-transparent" />
      <div
        className="pointer-events-auto bg-white/90 backdrop-blur-md border-t border-[#E5E7EB] px-4 pt-3"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0) + 12px)" }}
      >
        <Link
          to="/booking"
          className="group flex items-center justify-center gap-2 w-full h-[52px] rounded-2xl bg-[#2D4A3E] text-white text-[15px] font-semibold shadow-[0_6px_20px_rgba(45,74,62,0.24)] hover:bg-[#1f3629] active:scale-[0.99] transition-all"
          data-testid="mobile-book-appointment-bar-cta"
          aria-label="Book an appointment"
        >
          <Calendar className="w-4 h-4" aria-hidden="true" />
          <span>Book Appointment</span>
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}
